import {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';

import {handleErrorResponse} from '$utils/axios';
import toast from '$utils/toast';
import {
  getPricingList,
  postCreatePricingEntry,
  putUpdatePricingEntry,
  deletePricingEntry,
  getCategoriesList,
} from '~/api/finance/pricingList/pricingListApis';

import {ToastType} from '$components/Toast/constant';

interface PricingListErrorResponse {
  success: boolean;
  errors?: string[];
  message: string;
}

export function usePricingList({
  categoryId,
  search,
  sort_by,
  sort_direction,
  per_page,
  page_no,
}: IUsePricingListParams) {
  const {data, isLoading} = useQuery({
    queryKey: [
      'pricing-list',
      categoryId,
      search,
      sort_by,
      sort_direction,
      per_page,
      page_no,
    ],
    queryFn: () =>
      getPricingList({
        categoryId,
        search,
        sort_by,
        sort_direction,
        per_page,
        page_no,
      }),
    enabled: !!categoryId,
  });

  return {
    data: data ?? {
      pricingList: [],
      totalPages: 0,
      totalRecords: 0,
    },
    isLoading,
  };
}

export function useCreatePricingEntry(onSuccess: () => void) {
  const queryClient = useQueryClient();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {mutate, isPending} = useMutation({
    mutationKey: ['pricing-entry-create'],
    mutationFn: postCreatePricingEntry,
    onSuccess: data => {
      toast(ToastType.Success, 'Create pricing entry', data.message);
      queryClient.invalidateQueries({queryKey: ['pricing-list']});
      onSuccess();
      setError(null);
    },
    onError: (error: AxiosError<PricingListErrorResponse>) => {
      const errorMessage =
        error.response?.data?.errors?.[0] ||
        error.response?.data?.message ||
        'Failed to create pricing entry';
      setError(errorMessage);
      toast(ToastType.Error, 'Create pricing entry failed', errorMessage);
    },
    onSettled: () => setDisableSubmit(false),
  });

  const sendCreatePricingEntryRequest = (payload: IPostPricingEntryPayload) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {sendCreatePricingEntryRequest, isPending, disableSubmit, error};
}

export function useUpdatePricingEntry(onSuccess: () => void) {
  const queryClient = useQueryClient();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate, isPending} = useMutation({
    mutationKey: ['pricing-entry-update'],
    mutationFn: putUpdatePricingEntry,
    onSuccess: data => {
      toast(ToastType.Success, 'Update pricing entry', data.message);
      queryClient.invalidateQueries({queryKey: ['pricing-list']});
      onSuccess();
    },
    onError: error => handleErrorResponse(error, 'Update pricing entry failed'),
    onSettled: () => setDisableSubmit(false),
  });

  const sendUpdatePricingEntryRequest = (payload: IPutPricingEntryPayload) => {
    setDisableSubmit(true);
    mutate(payload);
  };

  return {sendUpdatePricingEntryRequest, isPending, disableSubmit};
}

export function useDeletePricingEntry(onSuccess: () => void) {
  const queryClient = useQueryClient();
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate: deletePricingEntryRequest, isPending} = useMutation({
    mutationKey: ['pricing-entry-delete'],
    mutationFn: deletePricingEntry,
    onSuccess: data => {
      toast(ToastType.Success, 'Delete pricing entry', data.message);
      queryClient.invalidateQueries({queryKey: ['pricing-list']});
      onSuccess();
    },
    onError: error => handleErrorResponse(error, 'Delete pricing entry'),
    onSettled: () => setDisableSubmit(false),
  });

  return {
    deletePricingEntryRequest,
    isDeleting: isPending,
    disableSubmit,
  };
}

export function useCategoriesList() {
  const {data, isLoading} = useQuery({
    queryKey: ['categories'],
    queryFn: getCategoriesList,
  });

  return {
    data: data?.data || [],
    isLoading,
  };
}
